import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import MarkdownField from '../../components/admin/MarkdownField.jsx';

const BASE = import.meta.env.VITE_API_BASE || '/api';

const CN_DIGITS = '〇一二三四五六七八九';

// Year reads digit-by-digit (2026 → 二〇二六).
function toCnNum(n) {
  return String(n).split('').map(d => CN_DIGITS[parseInt(d)] || d).join('');
}

// Months (1–12) and days (1–31) read as cardinals (16 → 十六, 20 → 二十,
// 31 → 三十一), never digit-by-digit.
function toCnCardinal(n) {
  if (n < 10) return CN_DIGITS[n];
  if (n === 10) return '十';
  if (n < 20) return '十' + CN_DIGITS[n - 10];
  const tens = Math.floor(n / 10), ones = n % 10;
  return CN_DIGITS[tens] + '十' + (ones ? CN_DIGITS[ones] : '');
}

function formatDateEn(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const date = new Date(+y, +m - 1, +d);
  return `${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} · ${y}`;
}

function formatDateCn(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${toCnNum(y)}年${toCnCardinal(parseInt(m))}月${toCnCardinal(parseInt(d))}日`;
}

function formatDate(iso, lang) {
  return lang === 'en' ? formatDateEn(iso) : formatDateCn(iso);
}

const S = {
  back: {
    fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase',
    color: 'var(--bronze)', cursor: 'pointer', background: 'none', border: 'none',
    display: 'block', marginBottom: 20, padding: 0,
  },
  h1: { fontFamily: 'var(--display)', fontSize: 32, fontWeight: 400, margin: '0 0 28px' },
  label: {
    fontFamily: 'var(--label)', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase',
    display: 'block', marginBottom: 6, color: 'var(--slate-70)',
  },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid var(--bronze-30)', background: 'var(--white)',
    fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)',
    outline: 'none', marginBottom: 18,
  },
  textarea: {
    width: '100%', minHeight: 80, padding: 12,
    border: '1px solid var(--bronze-30)', background: 'var(--white)',
    fontFamily: 'var(--body)', fontSize: 14, color: 'var(--slate)',
    outline: 'none', marginBottom: 18, resize: 'vertical', lineHeight: 1.5,
  },
  row: { display: 'flex', gap: 24 },
  col: { flex: 1, minWidth: 0 },
  saveBtn: {
    padding: '12px 32px', background: 'var(--slate)', color: 'var(--bone)',
    border: 0, fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.24em',
    textTransform: 'uppercase', cursor: 'pointer',
  },
  deleteBtn: {
    padding: '12px 32px', background: 'none', color: '#c44',
    border: '1px solid #c44', fontFamily: 'var(--label)', fontSize: 11,
    letterSpacing: '.24em', textTransform: 'uppercase', cursor: 'pointer',
    marginLeft: 12,
  },
  langBtn: (active) => ({
    padding: '8px 20px', cursor: 'pointer',
    fontFamily: 'var(--label)', fontSize: 11, letterSpacing: '.2em', textTransform: 'uppercase',
    background: active ? 'var(--slate)' : 'var(--white)',
    color: active ? 'var(--bone)' : 'var(--slate-70)',
    border: '1px solid var(--bronze-30)',
  }),
};

const EMPTY = { lang: 'cn', cat: '', title: '', excerpt: '', body_md: '', date: '', _dateIso: '', published: false };

export default function InsightEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(isNew ? { ...EMPTY } : null);

  useEffect(() => {
    if (isNew) { setForm({ ...EMPTY }); return; }
    // Use the api client (Bearer token) so unpublished drafts load too.
    api.insight(id)
      .then(d => {
        if (!d) return;
        const init = {
          lang: d.lang || 'cn',
          cat: d.cat || '',
          title: d.title || '',
          excerpt: d.excerpt || '',
          body_md: d.body_md || '',
          date: d.date || '',
          _dateIso: d.date_iso || '',
          published: !!d.published,
        };
        setForm(init);
      })
      .catch(() => {});
  }, [id]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const setLang = (lang) => setForm(prev => ({
    ...prev,
    lang,
    // Re-format the date for the new language if we have an ISO value.
    date: prev._dateIso ? formatDate(prev._dateIso, lang) : prev.date,
  }));

  const handleSave = async () => {
    const payload = {
      lang: form.lang,
      cat: form.cat,
      date: form.date,
      date_iso: form._dateIso || null,
      title: form.title,
      excerpt: form.excerpt,
      body_md: form.body_md,
      published: form.published,
    };
    try {
      if (isNew) await api.createInsight(payload);
      else await api.updateInsight(id, payload);
      navigate('/admin');
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this article?')) return;
    try {
      await api.deleteInsight(id);
      navigate('/admin');
    } catch (e) { alert(e.message); }
  };

  if (!form) return null;

  return (
    <div>
      <button style={S.back} onClick={() => navigate('/admin')}>← Back</button>
      <h1 style={S.h1}>{isNew ? 'New Article' : 'Edit Article'}</h1>

      <div style={{ marginBottom: 24 }}>
        <label style={S.label}>Language</label>
        <div style={{ display: 'flex' }}>
          <button type="button" style={S.langBtn(form.lang === 'cn')} onClick={() => setLang('cn')}>中文</button>
          <button type="button" style={S.langBtn(form.lang === 'en')} onClick={() => setLang('en')}>English</button>
        </div>
      </div>

      <div style={S.row}>
        <div style={S.col}>
          <label style={S.label}>Title</label>
          <input style={S.input} value={form.title} onChange={e => set('title', e.target.value)} />
        </div>
        <div style={S.col}>
          <label style={S.label}>Category</label>
          <input style={S.input} value={form.cat} onChange={e => set('cat', e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={S.label}>Date</label>
        <input type="date" value={form._dateIso || ''}
          onChange={e => {
            const iso = e.target.value;
            set('_dateIso', iso);
            set('date', formatDate(iso, form.lang));
          }}
          style={{
            padding: '10px 12px',
            border: '1px solid var(--bronze-30)', background: 'var(--white)',
            fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)',
            outline: 'none',
          }} />
        <span style={{ marginLeft: 18, fontFamily: 'var(--body)', fontSize: 15, color: 'var(--bronze)', letterSpacing: '.04em' }}>
          {form.date || '—'}
        </span>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={S.label}>Excerpt</label>
        <textarea style={S.textarea} value={form.excerpt} onChange={e => set('excerpt', e.target.value)} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <MarkdownField label="Body" value={form.body_md} onChange={v => set('body_md', v)} />
      </div>

      <div style={{ marginBottom: 28, padding: '14px 16px', background: 'var(--bone-soft)', border: '1px solid var(--bronze-30)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--body)', fontSize: 16, color: 'var(--slate)', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.published} onChange={e => set('published', e.target.checked)} />
          <span><strong>{form.published ? 'Published' : 'Draft'}</strong> — {form.published ? 'visible on the public site' : 'hidden until you publish it'}</span>
        </label>
      </div>

      <div>
        <button style={S.saveBtn} onClick={handleSave}>Save</button>
        {!isNew && <button style={S.deleteBtn} onClick={handleDelete}>Delete</button>}
      </div>
    </div>
  );
}
